import { Select as BaseSelect } from "@base-ui/react/select";
import type { ReactNode } from "react";
import { cx } from "./cx.js";
import { CheckIcon, ChevronUpDownIcon } from "./icons.jsx";

export interface SelectItem<V extends string = string> {
  value: V;
  label: ReactNode;
}

export interface SelectProps<V extends string> {
  items: ReadonlyArray<SelectItem<V>>;
  name?: string;
  value?: V | null;
  defaultValue?: V | null;
  onValueChange?: (value: V) => void;
  placeholder?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  "aria-label"?: string;
}

/** Base UI Select styled as a Diotia control. */
export function Select<V extends string>({ items, name, value, defaultValue, onValueChange, placeholder, disabled, required, className, ...aria }: SelectProps<V>) {
  return (
    <BaseSelect.Root<V>
      items={items}
      name={name}
      value={value}
      defaultValue={defaultValue}
      onValueChange={(v) => {
        if (v !== null && onValueChange) onValueChange(v as V);
      }}
      disabled={disabled}
      required={required}
    >
      <BaseSelect.Trigger className={cx("dt-input", "dt-select", className)} {...aria}>
        <BaseSelect.Value placeholder={placeholder} className="dt-select__value" />
        <BaseSelect.Icon className="dt-select__icon">
          <ChevronUpDownIcon />
        </BaseSelect.Icon>
      </BaseSelect.Trigger>
      <BaseSelect.Portal>
        <BaseSelect.Positioner className="dt-popover-positioner" align="start" sideOffset={6} alignItemWithTrigger={false}>
          <BaseSelect.Popup className="dt-popover dt-select__popup">
            <BaseSelect.List className="dt-select__list">
              {items.map((item) => (
                <BaseSelect.Item key={item.value} value={item.value} className="dt-menu__item dt-select__item">
                  <BaseSelect.ItemText className="dt-select__item-text">{item.label}</BaseSelect.ItemText>
                  <BaseSelect.ItemIndicator className="dt-select__indicator">
                    <CheckIcon />
                  </BaseSelect.ItemIndicator>
                </BaseSelect.Item>
              ))}
            </BaseSelect.List>
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
}
